import React from "react";
import { eur, num } from "@/lib/format";

const BUCKETS = [0, 25, 50, 75, 100];

/**
 * Veränderungs-Ansicht im Excel-Stil (User-Vorlage Screenshot 08):
 *   Zeigt die Year-over-Year-Differenzen pro Erlösgruppe × WB-Bucket.
 *
 *   Layout:
 *     - Pro Erlösgruppe ein Block (analog `BilanzView`) mit 3 farbigen Spalten:
 *         Lagerwert-Δ / WB-Δ / Bilanz-Δ
 *     - Zeilen: 0 / 25 / 50 / 75 / 100 % + Summen-Zeile (Σ)
 *     - Oben ein „Gesamt"-Header mit den drei Summen-Δ (Lager / WB / Bilanz)
 *
 *   Farbcodierung pro Zelle:
 *     - Δ > 0  → grün (Gewinn / Zuwachs)
 *     - Δ < 0  → rot   (Verlust / Abbau)
 *     - Δ = 0  → neutral
 */
export default function VeraenderungView({ data }) {
    if (!data) return null;
    if (!data.vorjahr?.vorhanden) {
        return (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200">
                <strong>Kein Vorjahres-Snapshot vorhanden</strong> für {data.vorjahr?.zeitraum}.
                Lege zuerst eine Baseline für das Vorjahr an (siehe Button{" "}
                <em>„Baseline manuell anlegen"</em> auf dieser Seite).
            </div>
        );
    }
    const cells = data.zellen || [];
    const erloesgruppen = Array.from(new Set(cells.map((c) => c.erloesgruppe))).sort();
    const lookup = {};
    for (const c of cells) {
        lookup[`${c.erloesgruppe}::${c.wb_bucket}`] = c;
    }

    // Erlösgruppen in 2er-Paaren
    const paare = [];
    for (let i = 0; i < erloesgruppen.length; i += 2) {
        paare.push([erloesgruppen[i], erloesgruppen[i + 1] || null]);
    }

    return (
        <div className="space-y-8 bilanz-view" data-testid="snap-veraenderung-view">
            {/* Gesamt-Δ */}
            <div className="grid grid-cols-3 gap-4">
                <DeltaKpi
                    label="Δ Lagerwert"
                    delta={data.delta_summen.lagerwert}
                    aktuell={data.aktuell.summen.lagerwert}
                    vorjahr={data.vorjahr.summen.lagerwert}
                    color="lager"
                    testid="delta-lagerwert"
                />
                <DeltaKpi
                    label="Δ Wertberichtigung"
                    delta={data.delta_summen.wertberichtigung}
                    aktuell={data.aktuell.summen.wertberichtigung}
                    vorjahr={data.vorjahr.summen.wertberichtigung}
                    color="wb"
                    testid="delta-wb"
                />
                <DeltaKpi
                    label="Δ Bilanzwert"
                    delta={data.delta_summen.bilanzwert}
                    aktuell={data.aktuell.summen.bilanzwert}
                    vorjahr={data.vorjahr.summen.bilanzwert}
                    color="bilanz"
                    testid="delta-bilanz"
                />
            </div>

            {paare.map((paar, idx) => (
                <div key={idx} className="grid lg:grid-cols-2 gap-8 print:grid-cols-2 print:gap-6">
                    {paar.map((eg) => (
                        <ErloesgruppeDeltaBlock
                            key={eg || `empty-${idx}`}
                            erloesgruppe={eg}
                            lookup={lookup}
                            jahrAktuell={data.aktuell.zeitraum}
                            jahrVorjahr={data.vorjahr.zeitraum}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
}

function DeltaKpi({ label, delta, aktuell, vorjahr, color, testid }) {
    const positiv = delta > 0;
    const negativ = delta < 0;
    const sign = positiv ? "+" : "";
    const bgClass =
        color === "lager"
            ? "bg-amber-500/10 border-amber-500/40"
            : color === "wb"
                ? "bg-blue-500/10 border-blue-500/40"
                : "bg-emerald-500/10 border-emerald-500/40";
    const deltaColor = positiv
        ? "text-emerald-400"
        : negativ
            ? "text-red-400"
            : "text-muted-foreground";
    return (
        <div className={`rounded-md border p-3 ${bgClass}`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className={`font-heading text-2xl tracking-tight font-semibold tabular mt-1 ${deltaColor}`}>
                {sign}
                {eur(delta)}
            </div>
            <div className="text-[10px] text-muted-foreground/80 tabular mt-1 leading-tight">
                Aktuell {eur(aktuell)}
                <br />
                Vorjahr {eur(vorjahr)}
            </div>
        </div>
    );
}

function ErloesgruppeDeltaBlock({ erloesgruppe, lookup, jahrAktuell, jahrVorjahr }) {
    if (!erloesgruppe) return <div />;
    const kuerzel = abbrev(erloesgruppe);

    // Summen Δ
    let sumLagerD = 0, sumWBD = 0, sumBilanzD = 0;
    let sumLagerA = 0, sumLagerV = 0, sumWBA = 0, sumWBV = 0, sumBilanzA = 0, sumBilanzV = 0;
    for (const b of BUCKETS) {
        const c = lookup[`${erloesgruppe}::${b}`];
        if (c) {
            sumLagerD += c.delta.lagerwert || 0;
            sumWBD += c.delta.wertberichtigung || 0;
            sumBilanzD += c.delta.bilanzwert || 0;
            sumLagerA += c.aktuell.lagerwert || 0;
            sumLagerV += c.vorjahr.lagerwert || 0;
            sumWBA += c.aktuell.wertberichtigung || 0;
            sumWBV += c.vorjahr.wertberichtigung || 0;
            sumBilanzA += c.aktuell.bilanzwert || 0;
            sumBilanzV += c.vorjahr.bilanzwert || 0;
        }
    }

    return (
        <div className="bilanz-block" data-testid={`veraend-block-${erloesgruppe}`}>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1.5 print:text-black">
                {erloesgruppe}
            </div>

            <table className="bilanz-table w-full border-collapse text-sm tabular">
                <thead>
                    <tr>
                        <th className="bilanz-th-empty" />
                        <th className="bilanz-th bilanz-th-lager">
                            <div className="bilanz-th-kuerzel">{kuerzel}</div>
                            <div className="bilanz-th-label">Δ Lagerwert</div>
                            <div className="bilanz-th-date">
                                {jahrAktuell} − {jahrVorjahr}
                            </div>
                        </th>
                        <th className="bilanz-th bilanz-th-wb">
                            <div className="bilanz-th-kuerzel">{kuerzel}</div>
                            <div className="bilanz-th-label">Δ Wertberichtigung</div>
                            <div className="bilanz-th-date">
                                {jahrAktuell} − {jahrVorjahr}
                            </div>
                        </th>
                        <th className="bilanz-th bilanz-th-bilanz">
                            <div className="bilanz-th-kuerzel">{kuerzel}</div>
                            <div className="bilanz-th-label">Δ Bilanzwert</div>
                            <div className="bilanz-th-date">
                                {jahrAktuell} − {jahrVorjahr}
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {BUCKETS.map((b) => {
                        const c = lookup[`${erloesgruppe}::${b}`];
                        return (
                            <tr key={b}>
                                <td className="bilanz-row-label">{b}%</td>
                                <DeltaTd
                                    delta={c?.delta?.lagerwert}
                                    aktuell={c?.aktuell?.lagerwert}
                                    vorjahr={c?.vorjahr?.lagerwert}
                                    klass="bilanz-td bilanz-td-lager"
                                />
                                <DeltaTd
                                    delta={c?.delta?.wertberichtigung}
                                    aktuell={c?.aktuell?.wertberichtigung}
                                    vorjahr={c?.vorjahr?.wertberichtigung}
                                    klass="bilanz-td bilanz-td-wb"
                                />
                                <DeltaTd
                                    delta={c?.delta?.bilanzwert}
                                    aktuell={c?.aktuell?.bilanzwert}
                                    vorjahr={c?.vorjahr?.bilanzwert}
                                    klass="bilanz-td bilanz-td-bilanz"
                                />
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr>
                        <td className="bilanz-foot-label">Σ</td>
                        <DeltaTd
                            delta={sumLagerD}
                            aktuell={sumLagerA}
                            vorjahr={sumLagerV}
                            klass="bilanz-foot bilanz-foot-lager"
                        />
                        <DeltaTd
                            delta={sumWBD}
                            aktuell={sumWBA}
                            vorjahr={sumWBV}
                            klass="bilanz-foot bilanz-foot-wb"
                        />
                        <DeltaTd
                            delta={sumBilanzD}
                            aktuell={sumBilanzA}
                            vorjahr={sumBilanzV}
                            klass="bilanz-foot bilanz-foot-bilanz"
                        />
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

function DeltaTd({ delta, aktuell, vorjahr, klass }) {
    const d = Number(delta || 0);
    const sign = d > 0 ? "+" : "";
    const deltaCol = d > 0 ? "text-emerald-300" : d < 0 ? "text-red-300" : "text-muted-foreground";
    return (
        <td className={klass}>
            <div className={`font-semibold ${deltaCol}`}>
                {sign}
                {eur(d)}
            </div>
            <div className="bilanz-td-sub">
                {eur(aktuell || 0)} <span className="opacity-50">vs.</span> {eur(vorjahr || 0)}
            </div>
        </td>
    );
}

// Erzeugt ein Kürzel wie in BilanzView.
function abbrev(name) {
    if (!name) return "";
    const cleaned = String(name).replace(/^\d+\s*:\s*/, "").trim();
    if (/kinder.*jugend/i.test(cleaned)) return "KJ";
    if (/annette\s*betz/i.test(cleaned)) return "AB";
    const words = cleaned.split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return cleaned.slice(0, 2).toUpperCase();
}

// helper export für num
export { num };
